import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DnaBackground } from "@/components/DnaBackground";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { authAPI } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { KeyRound, AlertCircle } from "lucide-react";
import { PasswordInput } from "@/components/PasswordInput";
import { PasswordStrengthIndicator } from "@/components/PasswordStrengthIndicator";
import { 
  calculatePasswordStrength, 
  getPasswordRequirements
} from "@/lib/password-validation";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

export default function ChangePassword() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  // Memoized password strength calculation
  const passwordStrength = useMemo(
    () => calculatePasswordStrength(newPassword, t),
    [newPassword, t]
  );

  // Memoized password requirements
  const passwordRequirements = useMemo(
    () => getPasswordRequirements(newPassword, t),
    [newPassword, t]
  );

  // Memoized form validation
  const isFormValid = useMemo(
    () => {
      return (
        currentPassword.trim() !== "" &&
        newPassword.trim() !== "" &&
        confirmPassword.trim() !== "" &&
        newPassword === confirmPassword &&
        newPassword.length >= 6
      );
    },
    [currentPassword, newPassword, confirmPassword]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!isFormValid) {
        return;
      }

      try {
        setLoading(true);
        const response = await authAPI.changePassword(currentPassword, newPassword);

        if (response.success) {
          toast({
            title: t("changePassword.successTitle"),
            description: t("changePassword.successDesc"),
          });
          
          // Redirect to dashboard after successful password change
          setTimeout(() => {
            const inviteToken = localStorage.getItem("invite_token");

            if (inviteToken) {
              navigate(`/invite/${inviteToken}`);
              return;
            }

            navigate("/dashboard");
          }, 1500);
        }
      } catch (error: any) {
        toast({
          title: t("common.error"),
          description: getLocalizedErrorMessage(error, "errors.passwordResetFailed"),
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    },
    [isFormValid, currentPassword, newPassword, navigate, toast, t]
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <DnaBackground />
      
      <Card className="w-full max-w-md bg-card border-border shadow-2xl">
        <div className="p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/20 mb-4">
              <KeyRound className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl font-bold mb-2">{t("changePassword.title")}</h1>
            <p className="text-muted-foreground">
              {t("changePassword.description")}
            </p>
          </div>

          {/* Security Notice */}
          <div className="mb-6 p-4 rounded-lg bg-primary/10 border border-primary/20">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-semibold text-foreground mb-1">
                  {t("changePassword.securityNoticeTitle")}
                </p>
                <p className="text-muted-foreground">
                  {t("changePassword.securityNoticeDesc")}
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Current Password */}
            <PasswordInput
              id="currentPassword"
              label={t("changePassword.currentPassword")}
              value={currentPassword}
              onChange={setCurrentPassword}
              required
              autoComplete="current-password"
            />

            {/* New Password */}
            <div className="space-y-2">
              <PasswordInput
                id="newPassword"
                label={t("changePassword.newPassword")}
                value={newPassword}
                onChange={setNewPassword}
                required
                autoComplete="new-password"
              />
              
              {/* Password Strength Indicator */}
              {newPassword && (
                <PasswordStrengthIndicator
                  strength={passwordStrength}
                  requirements={passwordRequirements}
                  strengthLabel={t("changePassword.strength")}
                />
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <PasswordInput
                id="confirmPassword"
                label={t("changePassword.confirmPassword")}
                value={confirmPassword}
                onChange={setConfirmPassword}
                required
                autoComplete="new-password"
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {t("changePassword.passwordsMismatch")}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90 shadow-glow h-12 text-base font-semibold"
              disabled={loading || !isFormValid}
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t("changePassword.changing")}
                </div>
              ) : (
                t("changePassword.changeButton")
              )}
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
}
